/**
 * Constants and helpers for the Filing Info modal.
 * @module filingInfoModal.utils
 */

import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import { FormOptionsModel } from "../../../data-model/formOptions";
import { FILING_MODE } from "../../../enums/filingMode";
import { getAllRelevantOptions } from "../../../utils/formOptions";
import { formatFaxNumberForSave } from "../../../utils/stringModifications";

/** Payload emitted when the Filing Info modal is saved. */
export interface FilingInfoSavePayload {
  filingMode: string;
  filingDetail: string;
}

/** form_options document ids used by the details SearchableCard. */
const FORM_OPTION_DOC_ID = {
  PORTAL: "portal_name",
  FAX: "fax_number",
} as const;

const MODES_REQUIRING_DETAIL = new Set<string>([
  FILING_MODE.PORTAL,
  FILING_MODE.FAX,
]);

/**
 * Normalises a filing detail for the selected mode.
 *
 * Fax details are phone numbers, so they are run through
 * {@link formatFaxNumberForSave} (`+1-XXX-XXX-XXXX`). Portal names (and any
 * other mode) are returned untouched. Formatting is idempotent, so
 * already-formatted values pass through unchanged.
 *
 * @param filingMode - Selected filing mode
 * @param filingDetail - Raw portal name or fax number entered or picked
 * @return Formatted phone number for fax, otherwise the original detail
 */
export const formatFilingDetail = (
  filingMode: string,
  filingDetail: string,
): string => {
  if (filingMode !== FILING_MODE.FAX || !filingDetail) {
    return filingDetail;
  }
  return formatFaxNumberForSave(filingDetail);
};

/**
 * Maps form_options docs onto SearchableCard options for the selected mode.
 *
 * Portal reads the `portal_name` document; fax reads `fax_number`. Other
 * modes have no details catalog.
 *
 * @param filingMode - Selected filing mode
 * @param formOptions - Documents loaded into the formOptions slice
 * @return Label/value options for the details SearchableCard
 */
export const getFilingDetailOptions = (
  filingMode: string,
  formOptions: FormOptionsModel[] | undefined,
): SearchableCardOption[] => {
  const docId =
    filingMode === FILING_MODE.PORTAL
      ? FORM_OPTION_DOC_ID.PORTAL
      : filingMode === FILING_MODE.FAX
        ? FORM_OPTION_DOC_ID.FAX
        : undefined;
  if (!docId) {
    return [];
  }
  const rawOptions = getAllRelevantOptions(formOptions ?? [], docId) ?? [];
  return rawOptions.flatMap((option) =>
    option.label && option.value
      ? [{ label: option.label, value: option.value }]
      : [],
  );
};

/**
 * Whether Save is allowed for the current filing-info selection.
 *
 * A filing mode is always required. Portal and fax also need a non-empty
 * `filingDetail` (after trim); call and not-applicable do not.
 *
 * @param filingMode - Selected filing mode, or empty when none is chosen
 * @param filingDetail - Portal name or fax number for modes that require it
 * @return True when the Save button should be enabled
 */
export const isFilingInfoSaveEnabled = (
  filingMode: string,
  filingDetail: string,
): boolean => {
  if (!filingMode.trim()) {
    return false;
  }
  if (MODES_REQUIRING_DETAIL.has(filingMode)) {
    return filingDetail.trim().length > 0;
  }
  return true;
};
